import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { SlidersHorizontal, Loader2, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { inventoryApi } from "./inventoryApi";
import { ADJUSTMENT_TYPES, ADJUSTMENT_TYPE_MAP } from "./specializedConstants";
import { TRANSACTION_LABELS, formatDateTime } from "./inventoryConstants";

export const AdjustmentsSection = ({ currentUser, canManage }) => {
  const [parts, setParts] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partId, setPartId] = useState("");
  const [type, setType] = useState("count_correction");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([inventoryApi.getParts(), inventoryApi.getAdjustments()]);
      setParts(p); setAdjustments(a);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const selectedPart = parts.find((p) => p.id === partId);
  const dir = ADJUSTMENT_TYPE_MAP[type]?.direction;

  const submit = async () => {
    if (!partId) { toast.error("Select a part"); return; }
    if (qty === "" || isNaN(Number(qty))) { toast.error("Enter a quantity"); return; }
    const body = { part_id: partId, adjustment_type: type, reason, notes, user: userName };
    if (dir === "set") body.new_quantity = Number(qty);
    else if (dir === "+") body.delta = Math.abs(Number(qty));
    else body.delta = -Math.abs(Number(qty));
    setSaving(true);
    try {
      await inventoryApi.createAdjustment(body);
      toast.success("Adjustment recorded");
      setQty(""); setReason(""); setNotes("");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="adjustments-section">
      {canManage && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2"><SlidersHorizontal className="w-4 h-4 text-amber-500" /> New Adjustment</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">Part</Label>
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={partId} onChange={(e) => setPartId(e.target.value)} data-testid="adj-part-select">
                <option value="">Select part...</option>
                {parts.map((p) => <option key={p.id} value={p.id}>{p.part_number} — {p.name} (on hand {p.quantity_on_hand})</option>)}
              </select>
            </div>
            <div><Label className="text-xs text-slate-500">Adjustment Type</Label>
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={type} onChange={(e) => setType(e.target.value)} data-testid="adj-type-select">
                {ADJUSTMENT_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            <div><Label className="text-xs text-slate-500">{dir === "set" ? "New Counted Quantity" : "Quantity"}</Label>
              <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="adj-qty-input" />
              {selectedPart && <span className="text-[10px] text-slate-400">Current on hand: {selectedPart.quantity_on_hand}</span>}
            </div>
            <div><Label className="text-xs text-slate-500">Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional" /></div>
          </div>
          <div><Label className="text-xs text-slate-500">Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <Button onClick={submit} disabled={saving} data-testid="adj-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Record Adjustment"}</Button>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-2">Recent Adjustments</h3>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : adjustments.length === 0 ? (
          <div className="text-center py-8 text-slate-400">No adjustments recorded</div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
            {adjustments.map((tx) => {
              const cfg = TRANSACTION_LABELS[tx.transaction_type] || {};
              const up = (tx.quantity || 0) > 0;
              const chip = up ? "bg-green-50" : "bg-red-50";
              const Icon = up ? ArrowDownCircle : ArrowUpCircle;
              const ic = up ? "text-green-600" : "text-red-600";
              return (
                <div key={tx.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors" data-testid="adjustment-row">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${chip}`}><Icon className={`w-4 h-4 ${ic}`} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{tx.part_name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${cfg.bg || "bg-slate-100"} ${cfg.color || "text-slate-600"}`}>{(tx.adjustment_type || cfg.label || "").replace(/_/g, " ")}</span>
                      <span className="text-slate-400"> · {formatDateTime(tx.date)} · {tx.user}{tx.reason ? ` · ${tx.reason}` : ""}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-sm font-semibold tabular-nums ${up ? "text-green-600" : "text-red-600"}`}>{up ? "+" : ""}{tx.quantity}</div>
                    <div className="text-[10px] text-slate-400 tabular-nums">{tx.previous_quantity} → {tx.new_quantity}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
